import { isDefined } from "@clipboard-health/util-ts";
import { useLazyIsInsideGeofence } from "@src/appV2/Location/hooks/useIsInsideGeofence";
import { type GeoLocation } from "@src/appV2/Location/types";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { addHours } from "date-fns";

import { CHARTING_ERROR_CODES, CHARTING_REVIEW_DURATION_IN_HOURS } from "../constants";
import { usePatchChartingNoncompliances } from "./usePatchChartingNoncompliances";

interface UseMarkChartingForReviewParams {
  workerId: string;
  workplaceId: string;
  chartingNoncomplianceId: string;
  workplaceCoordinates: GeoLocation | undefined;
}

interface MarkChartingForReviewResult {
  isInsideGeofence: boolean;
}

export function useMarkChartingForReview({
  workerId,
  workplaceId,
  chartingNoncomplianceId,
  workplaceCoordinates,
}: UseMarkChartingForReviewParams): UseMutationResult<MarkChartingForReviewResult, Error, void> {
  const { mutateAsync: patchChartingNoncompliance } = usePatchChartingNoncompliances({
    chartingNoncomplianceId,
  });

  const getIsInsideGeofence = useLazyIsInsideGeofence({
    requestUniqueId: {
      workerId,
      shiftId: workplaceId,
    },
    action: "verify-charting",
    fallbackGeofenceLocation: workplaceCoordinates,
  });

  return useMutation<MarkChartingForReviewResult, Error>({
    mutationFn: async () => {
      if (!workplaceCoordinates) {
        throw new Error(CHARTING_ERROR_CODES.MISSING_COORDINATES);
      }

      // Keep loader visible for at least 1s
      await Promise.resolve(new Promise((resolve) => setTimeout(resolve, 1000)));

      const result = await getIsInsideGeofence();
      const isInside =
        isDefined(result.data?.isInsideGeofence) && result.data.isInsideGeofence && !result.error;

      if (!isInside) {
        // Use a stable error message code the UI can branch on
        throw new Error(CHARTING_ERROR_CODES.OUTSIDE_GEOFENCE);
      }

      try {
        await patchChartingNoncompliance({
          data: {
            type: "charting-noncompliance",
            id: chartingNoncomplianceId,
            attributes: {
              deadlineToReview: addHours(new Date(), CHARTING_REVIEW_DURATION_IN_HOURS),
            },
          },
        });
      } catch {
        throw new Error(CHARTING_ERROR_CODES.PATCH_FAILED);
      }

      return { isInsideGeofence: true };
    },
  });
}
